import Head from "next/head";
import Image from "next/image";
import { useEffect, useRef } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Header from "../components/Header";
import Title from "../components/Title";
import DevsSection from "../components/_parceiras/DevsSection";
import FormSection from "../components/_parceiras/FormSection";
import ProjectsSection from "../components/_parceiras/ProjectsSection";
import { partnersLogos } from "../shared/utils/partners-logos";
import styles from "../styles/pages/parceiras.module.scss";
import { BsFillCheckCircleFill } from "react-icons/bs";
import {
  partnershipBenefits,
  partnershipSteps,
} from "../shared/utils/partners";
import PartnershipCard from "../components/_parceiras/PartnershipCard";
import FloatLinksPartners from "../components/FloatLinksPartners";

export default function Parceiras() {
  const ref = useRef<HTMLDivElement>(null);

  function scrollToForm() {
    if (ref.current) {
      window.scrollTo({ top: ref.current.offsetTop - 100 });
    }
  }

  useEffect(() => {
    AOS.init({
      disable: "mobile",
      duration: 700,
      easing: "ease-out-cubic",
      once: true,
    });
  }, []);

  return (
    <>
      <Head>
        <title>Parceiras | Kenzie Academy Brasil</title>
        <meta
          name="description"
          content="Seja uma empresa parceira da Kenzie!"
        />
        <meta
          name="twitter:title"
          content="Parceiras | Kenzie Academy Brasil"
        />
        <meta
          name="twitter:description"
          content="Seja uma empresa parceira da Kenzie!"
        />
        <meta property="og:title" content="Parceiras | Kenzie Academy Brasil" />
        <link rel="canonical" href="https://kenzie.com.br/parceiras" />
      </Head>

      <Header onClick={scrollToForm} />

      <main>
        <section className={styles.wrapper}>
          <div className={styles.background}>
            <div className={styles.container}>
              <div className={styles.contentContainer} data-aos="fade-right">
                <h1 className={styles.title}>
                  Seja uma empresa parceira da Kenzie
                </h1>
                <p>
                  Contrate os melhores programadores e programadoras de forma
                  rápida e sem custo. Encontre profissionais que parecem feitos
                  sob medida para a sua organização.
                </p>
                <button className={styles.cta} onClick={scrollToForm}>
                  <span> Quero ser parceira</span>
                  <svg width="56" height="16" viewBox="0 0 56 16" fill="none">
                    <path
                      d="M55.2765 8.42196C55.667 8.03143 55.667 7.39827 55.2765 7.00774L48.9125 0.643781C48.522 0.253257 47.8888 0.253257 47.4983 0.643781C47.1078 1.03431 47.1078 1.66747 47.4983 2.05799L53.1551 7.71485L47.4983 13.3717C47.1078 13.7622 47.1078 14.3954 47.4983 14.7859C47.8888 15.1764 48.522 15.1764 48.9125 14.7859L55.2765 8.42196ZM0.388794 8.71484L54.5694 8.71485L54.5694 6.71485L0.388794 6.71484L0.388794 8.71484Z"
                      fill="#fff"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
        <FormSection refProp={ref} />

        <section className="container">
          <div className={styles.partnership} data-aos="fade-left">
            <Title>Por que ser uma empresa parceira?</Title>
            <ul>
              {partnershipBenefits.map((benefit, index) => (
                <li key={`benefit${index}`}>
                  <BsFillCheckCircleFill />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="container">
          <div data-aos="fade-down">
            <Title>Como funciona a parceria?</Title>
            <ul className={styles.partnershipSteps}>
              {partnershipSteps.map((step, index) => (
                <PartnershipCard
                  key={`PartnershipCard${index}`}
                  backgroundColor={step.backgroundColor}
                  cardNumber={step.cardNumber}
                  numberColor={step.numberColor}
                  paragraph={step.paragraph}
                  paragraphColor={step.paragraphColor}
                />
              ))}
            </ul>
            <button
              className={`${styles.cta} ${styles.blue}`}
              onClick={scrollToForm}
            >
              <span> Quero ser parceira</span>
              <svg width="56" height="16" viewBox="0 0 56 16" fill="none">
                <path
                  d="M55.2765 8.42196C55.667 8.03143 55.667 7.39827 55.2765 7.00774L48.9125 0.643781C48.522 0.253257 47.8888 0.253257 47.4983 0.643781C47.1078 1.03431 47.1078 1.66747 47.4983 2.05799L53.1551 7.71485L47.4983 13.3717C47.1078 13.7622 47.1078 14.3954 47.4983 14.7859C47.8888 15.1764 48.522 15.1764 48.9125 14.7859L55.2765 8.42196ZM0.388794 8.71484L54.5694 8.71485L54.5694 6.71485L0.388794 6.71484L0.388794 8.71484Z"
                  fill="#0000FF"
                />
              </svg>
            </button>
          </div>
        </section>

        <DevsSection />

        <section className={styles.partnersContainer}>
          <div className={styles.partnersTitle}>
            <Title>Quem já está conosco</Title>
          </div>
          <ul className={styles.partners}>
            {partnersLogos.map((partner) => (
              <li key={partner.name}>
                <Image
                  src={partner.image}
                  alt={`Logo da empresa ${partner.name}`}
                  width={97}
                  height={59}
                  loading="lazy"
                />
              </li>
            ))}
          </ul>
          <p className={styles.partnersParagraph}>
            <strong>Mais de 500 parceiros</strong> impulsionam carreiras e
            transformam vidas junto da Kenzie.{" "}
            <strong> Vem com a gente! </strong>
          </p>
        </section>
        <ProjectsSection />
      </main>
      <FloatLinksPartners />
    </>
  );
}
